// src/components/issues/NavigationSuite.tsx — SCE Issues
// « Problème suivant » (01/10, piste A retenue par Guillaume) : deux flèches
// sur le voile, de part et d'autre de la fiche en surimpression, et la
// position dans la liste au-dessus (« À qualifier · 3 sur 7 »).
//
// Maquette : https://claude.ai/artifact/A1CcJPk5w1XRhL6YwRj2jo
//
// ─── Choix ───
//
//  - Bureau seulement : monté par IssueDetailPage quand la fiche est en
//    surimpression (au-delà de 768 px), flèches visibles à partir de 960 px —
//    en dessous, elles mordraient sur la fiche ; le clavier reste.
//  - Rien sur la fiche elle-même : tout est sur le voile, par un portail dans
//    l'ancre de ModaleRoute. Au survol, un aperçu dit où mène la flèche.
//  - ← / → au clavier, sauf dans un champ de saisie.
//  - Aller au suivant REMPLACE l'entrée d'historique : la croix referme
//    toujours d'un coup, et « Précédent » du navigateur ne repasse pas par
//    chaque fiche vue. La suite repart dans l'état, figée.
//  - Une saisie en cours (commentaire, formulaire ouvert) est confirmée avant
//    de partir : elle serait perdue. Par la confirmation de l'appli (02/11),
//    plus par la fenêtre du navigateur.

import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useAncreVoile, type EtatModale } from '@/components/layout/ModaleRoute'
import { Confirmation } from '@/components/ui/Confirmation'
import { METIERS_COULEURS } from '@/config/metiers.config'
import type { Issue } from '@/types/issue.types'
import { lienProbleme } from '@/utils/liensFiches'
import { lireSuite, positionDans, suiteDeLaFiche } from '@/utils/suiteProblemes'

export function NavigationSuite({
  idCourant,
  tous,
}: {
  idCourant: string
  /** Tous les problèmes lus : suite par défaut, et contenu des aperçus. */
  tous: readonly Issue[]
}): JSX.Element | null {
  const navigate = useNavigate()
  const location = useLocation()
  const ancre = useAncreVoile()

  const etat = (location.state ?? {}) as EtatModale
  const origine = useMemo(() => lireSuite(etat.suite), [etat.suite])
  const suite = useMemo(() => suiteDeLaFiche(origine, tous, idCourant), [origine, tous, idCourant])
  const position = suite ? positionDans(suite, idCourant) : null
  const parId = useMemo(() => new Map(tous.map((i) => [i.id, i])), [tous])
  /** Le problème visé quand une saisie en cours attend confirmation. */
  const [enAttente, setEnAttente] = useState<string | null>(null)

  const partir = useCallback(
    (cible: string) => {
      if (!suite) return
      const etatCourant = (location.state ?? {}) as EtatModale
      // `evenement` ne suit pas : il appartient au problème qu'on quitte.
      const { evenement: _quitte, ...reste } = etatCourant
      void _quitte
      navigate(lienProbleme(cible), { replace: true, state: { ...reste, suite } })
    },
    [suite, location.state, navigate],
  )

  const aller = useCallback(
    (cible: string) => {
      if (!suite) return
      if (saisieEnCours()) {
        setEnAttente(cible)
        return
      }
      partir(cible)
    },
    [suite, partir],
  )

  useEffect(() => {
    function surTouche(e: KeyboardEvent): void {
      // `defaultPrevented` : la visionneuse de photos, ouverte par-dessus, a
      // déjà pris la flèche pour elle. Une confirmation ouverte garde les flèches.
      if (enAttente || e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
      if (estChampDeSaisie(e.target)) return
      const cible = e.key === 'ArrowLeft' ? position?.precedent : position?.suivant
      if (!cible) return
      e.preventDefault()
      aller(cible)
    }
    window.addEventListener('keydown', surTouche)
    return () => window.removeEventListener('keydown', surTouche)
  }, [position, aller, enAttente])

  if (!ancre || !suite || !position || position.total < 2) return null

  return createPortal(
    <>
      {enAttente && (
        <Confirmation
          titre="Changer de problème ?"
          confirmer="Changer quand même"
          annuler="Rester"
          danger
          onConfirmer={() => {
            const cible = enAttente
            setEnAttente(null)
            partir(cible)
          }}
          onAnnuler={() => setEnAttente(null)}
        >
          Votre saisie en cours sur ce problème sera perdue.
        </Confirmation>
      )}
      <div className="suite-compteur">
        <span style={{ opacity: 0.8 }}>{suite.libelle}</span>
        <span style={{ fontWeight: 500 }}>
          {position.rang} sur {position.total}
        </span>
      </div>
      {position.precedent && (
        <Fleche sens="precedent" id={position.precedent} issue={parId.get(position.precedent)} onAller={aller} />
      )}
      {position.suivant && (
        <Fleche sens="suivant" id={position.suivant} issue={parId.get(position.suivant)} onAller={aller} />
      )}
    </>,
    ancre,
  )
}

function Fleche({
  sens,
  id,
  issue,
  onAller,
}: {
  sens: 'precedent' | 'suivant'
  id: string
  /** Absent tant que la liste complète n'est pas lue : l'aperçu se contente de l'ID. */
  issue: Issue | undefined
  onAller: (id: string) => void
}): JSX.Element {
  const suivant = sens === 'suivant'
  return (
    <div className={`suite-nav suite-nav--${sens}`}>
      <button
        type="button"
        className="suite-fleche"
        aria-label={`${suivant ? 'Problème suivant' : 'Problème précédent'} : ${id}${issue ? `, ${issue.titre}` : ''}`}
        onClick={(e) => {
          // Le voile referme la fiche au clic : la flèche ne doit pas y arriver.
          e.stopPropagation()
          onAller(id)
        }}
      >
        {suivant ? <ChevronRight size={24} aria-hidden="true" /> : <ChevronLeft size={24} aria-hidden="true" />}
      </button>
      <div className="suite-apercu" aria-hidden="true">
        <div className="flex items-center justify-between" style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
          <span>{suivant ? 'Suivant' : 'Précédent'}</span>
          <kbd className="suite-touche">{suivant ? '→' : '←'}</kbd>
        </div>
        <div className="flex items-center" style={{ gap: 6, fontSize: 13, color: 'var(--color-text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
          {issue && (
            <span style={{ width: 8, height: 8, borderRadius: 4, flexShrink: 0, backgroundColor: METIERS_COULEURS[issue.metier] }} />
          )}
          {[id, issue?.numeroSerie].filter(Boolean).join(' · ')}
        </div>
        {issue && <div className="suite-apercu-titre">{issue.titre}</div>}
      </div>
    </div>
  )
}

// ─── Aides ───

function estChampDeSaisie(cible: EventTarget | null): boolean {
  if (!(cible instanceof HTMLElement)) return false
  return cible.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(cible.tagName)
}

/**
 * Un texte tapé dans la fiche et pas encore enregistré : commentaire,
 * formulaire de modification, de clôture… Lu dans la page plutôt que demandé
 * à chaque formulaire : une zone de texte ajoutée demain est couverte d'office.
 * Un formulaire ouvert sur une valeur déjà enregistrée (description en cours
 * de modification) compte aussi — mieux vaut une question de trop qu'une
 * saisie perdue.
 */
function saisieEnCours(): boolean {
  const panneau = document.querySelector('.modale-route-panel')
  if (!panneau) return false
  return Array.from(panneau.querySelectorAll('textarea')).some((t) => t.value.trim() !== '')
}
